import type { Id } from "@Aspecto/backend/convex/_generated/dataModel";

import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BarChart3 } from "lucide-react";
import { useState } from "react";

import {
  attendanceOverviewActionLabel,
  attendanceOverviewCopy,
  eventsOverviewActionLabel,
  eventsOverviewCopy,
  formatRate,
  formatRateDeltaPpBadge,
  formatSignedCountPairDelta,
  GrowthBars,
  membersOverviewActionLabel,
  membersOverviewCopy,
  OverviewMetricCard,
  rsvpOverviewActionLabel,
  rsvpOverviewCopy,
  trendDirectionFromCounts,
  trendDirectionFromRates,
} from "@/components/analytics/analytics-widgets";
import { EventDetailModal } from "@/components/events/event-detail-modal";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/ui/section-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGroupLabel } from "@/hooks/use-group-label";
import { useGroupRouteData } from "@/hooks/use-group-route-data";
import { formatDate, formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/groups/$groupId/analytics")({
  component: GroupAnalyticsTab,
});

function GroupAnalyticsTab() {
  const groupLabel = useGroupLabel();
  const { groupId, isAdmin, group } = useGroupRouteData(Route.useParams().groupId);
  const [detailEventId, setDetailEventId] = useState<Id<"events"> | null>(null);

  const { data, isPending } = useQuery({
    ...convexQuery(api.analytics.getCommunityAnalytics, { groupId }),
    enabled: Boolean(group && isAdmin),
  });

  if (!group || !isAdmin) return null;

  if (isPending || data === undefined) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-4 max-w-xl rounded-md" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="min-h-[220px] rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-56 rounded-xl" />
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground">
        <BarChart3 className="h-12 w-12 opacity-50" aria-hidden />
        <p className="text-sm">
          You do not have access to analytics for this {groupLabel.singular}.
        </p>
      </div>
    );
  }

  const rsvpDisplay = data.pastEventsCount === 0 ? "—" : formatRate(data.rsvpRate);
  const attendanceDisplay = data.pastEventsCount === 0 ? "—" : formatRate(data.attendanceRate);

  const isOverviewEmptyState = data.membersCount <= 1 && data.eventsLast30Days === 0;

  return (
    <div className="space-y-6">
      <SectionCard
        title="Overview"
        description="Past 30 days only — activity and trends (UTC, by event start time)"
        contentClassName="pt-2"
      >
        {isOverviewEmptyState ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center md:py-10">
            <p className="max-w-md text-base text-foreground">
              This {groupLabel.singular} is just getting started
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="secondary"
                render={<Link to="/app/groups/$groupId/members" params={{ groupId }} />}
                nativeButton={false}
              >
                Invite members
              </Button>
              <Button
                variant="outline"
                render={<Link to="/app/groups/$groupId/events" params={{ groupId }} />}
                nativeButton={false}
              >
                Create event
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 *:min-w-0">
            <OverviewMetricCard
              label="Members"
              value={String(data.membersCount)}
              status={membersOverviewCopy(
                data.membersCount,
                data.membersJoinedLast30Days,
                data.membersJoinedPrevious30Days,
              )}
              trendDirection={trendDirectionFromCounts(
                data.membersJoinedLast30Days,
                data.membersJoinedPrevious30Days,
              )}
              trendLabel={formatSignedCountPairDelta(
                data.membersJoinedLast30Days,
                data.membersJoinedPrevious30Days,
              )}
              actionLabel={membersOverviewActionLabel(data.membersCount)}
              actionLink={<Link to="/app/groups/$groupId/members" params={{ groupId }} />}
            />
            <OverviewMetricCard
              label="Events"
              value={String(data.eventsCount)}
              status={eventsOverviewCopy(data.eventsLast30Days, data.eventsPrevious30Days)}
              trendDirection={trendDirectionFromCounts(
                data.eventsLast30Days,
                data.eventsPrevious30Days,
              )}
              trendLabel={formatSignedCountPairDelta(
                data.eventsLast30Days,
                data.eventsPrevious30Days,
              )}
              actionLabel={eventsOverviewActionLabel(data.eventsLast30Days)}
              actionLink={<Link to="/app/groups/$groupId/events" params={{ groupId }} />}
            />
            <OverviewMetricCard
              label="RSVP"
              value={rsvpDisplay}
              status={rsvpOverviewCopy(
                data.rsvpRate,
                data.rsvpRateLast30Days,
                data.rsvpRatePrevious30Days,
              )}
              trendDirection={trendDirectionFromRates(
                data.rsvpRateLast30Days,
                data.rsvpRatePrevious30Days,
              )}
              trendLabel={formatRateDeltaPpBadge(
                data.rsvpRateLast30Days,
                data.rsvpRatePrevious30Days,
              )}
              actionLabel={rsvpOverviewActionLabel(data.rsvpRate)}
              actionLink={<Link to="/app/groups/$groupId/events" params={{ groupId }} />}
            />
            <OverviewMetricCard
              label="Attendance"
              value={attendanceDisplay}
              status={attendanceOverviewCopy(
                data.attendanceRate,
                data.attendanceRateLast30Days,
                data.attendanceRatePrevious30Days,
              )}
              trendDirection={trendDirectionFromRates(
                data.attendanceRateLast30Days,
                data.attendanceRatePrevious30Days,
              )}
              trendLabel={formatRateDeltaPpBadge(
                data.attendanceRateLast30Days,
                data.attendanceRatePrevious30Days,
              )}
              actionLabel={attendanceOverviewActionLabel(data.rsvpRate)}
              actionLink={<Link to="/app/groups/$groupId/events" params={{ groupId }} />}
            />
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Recent events"
        description="Last 10 by start time (newest first). RSVP and attendance are totals for each event."
        contentClassName="pt-0"
      >
        {data.recentEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events yet.</p>
        ) : (
          <div className="-mx-1 overflow-x-auto">
            <div className="min-w-lg px-1">
              <div className="data-label grid grid-cols-[minmax(8rem,1fr)_minmax(10rem,auto)_3.5rem_5rem] items-center gap-3 border-b border-border px-3 py-2">
                <span>Name</span>
                <span>Date</span>
                <span className="text-right">RSVP</span>
                <span className="text-right">Attendance</span>
              </div>
              <ul className="divide-y divide-border">
                {data.recentEvents.map((row) => (
                  <li key={row.eventId}>
                    <button
                      type="button"
                      onClick={() => setDetailEventId(row.eventId)}
                      className={cn(
                        "grid w-full cursor-pointer grid-cols-[minmax(8rem,1fr)_minmax(10rem,auto)_3.5rem_5rem] items-center gap-3 px-3 py-3 text-left text-sm transition-colors",
                        "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                      )}
                    >
                      <span className="min-w-0 truncate font-medium text-foreground">
                        {row.title}
                      </span>
                      <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                        {formatDate(row.eventDate)} · {formatTime(row.eventTime)}
                      </span>
                      <span className="text-right tabular-nums text-foreground">
                        {row.rsvpCount}
                      </span>
                      <span className="text-right tabular-nums text-foreground">
                        {row.attendedCount}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SectionCard>

      <EventDetailModal eventId={detailEventId} onClose={() => setDetailEventId(null)} />

      <SectionCard
        title="Activity"
        description="Last 12 months (UTC). Member totals are cumulative through each month (current month through today)."
        contentClassName="space-y-0"
      >
        <GrowthBars title="Members by month" series={data.membersTotalByMonth} />
        <div className="mt-8 border-t border-border pt-8">
          <GrowthBars title="Events by month" series={data.eventGrowth} />
        </div>
      </SectionCard>
    </div>
  );
}
